import { Crud } from "@/api";
import { UploadsApi } from "@/api/uploads.api";
import Section from "@/components/Section/Section";
import { $uploads, UploadsService } from "@/stores/uploads";
import { toast } from "@/utils/toast";
import { REUSABLE_IMAGES } from "@/utils/constants/reusableImages";
import {
  MEDIA_OPTIONS,
  TREATMENT_OPTIONS,
  FRAME_GROUPS,
  ACRYLIC_OPTIONS,
  MAT_COLOR_OPTIONS,
  BACKING_COLOR_OPTIONS,
  MAT_PLY_OPTIONS,
  SUPPLIED_MEDIA_VALUE,
  CUSTOM_VALUE,
  FRAMED_TREATMENTS,
  PRODUCT_TYPE_TO_CATEGORY,
  computeOverallSize,
  deriveCustomerFacingFields,
  CANVAS_CONSTRUCTION_OPTIONS,
  CANVAS_MEDIA_OPTIONS,
  CANVAS_EDGE_OPTIONS,
  CANVAS_BORDER_COLOR_OPTIONS,
  HD_ALUMINUM_FINISH_OPTIONS,
  HD_ALUMINUM_MOUNT_OPTIONS,
  MOUNT_SUBSTRATE_OPTIONS,
  CUSTOM_ORDER_OPTIONS,
  PRINT_ON_OPTIONS,
} from "@/utils/content/framingSpecVocab";
import {
  DeepPartial,
  ImageFile,
  LineItem,
  LineItemCategory,
} from "@/utils/types/types";
import {
  Button,
  Checkbox,
  FileInput,
  NumberInput,
  Select,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useStore } from "@nanostores/react";
import { NotePencil, Plus, Trash } from "@phosphor-icons/react";
import { IconX } from "@tabler/icons-react";
import { useState, useRef, useEffect } from "react";
import "@mantine/tiptap/styles.css";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";

type LineItemFormProps = {
  lineItem?: LineItem;
  onSubmit?: () => void | Promise<void>;
  cartId: string;
};

export default function LineItemForm({
  cartId,
  lineItem,
  onSubmit,
}: LineItemFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileRemoved, setFileRemoved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedUploadedImage, setSelectedUploadedImage] =
    useState<ImageFile | null>(null);
  const [uploadsDropdownOpen, setUploadsDropdownOpen] = useState(false);
  const uploadsDropdownRef = useRef<HTMLDivElement>(null);
  const uploadedImages = useStore($uploads);
  const previousImageUrl = lineItem?.itemJson.imageUrl || "";
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: lineItem?.itemJson.description || "",
  });

  const filePreviewUrl = file
    ? URL.createObjectURL(file)
    : selectedUploadedImage
      ? selectedUploadedImage.thumbnailUrl || selectedUploadedImage.url
      : fileRemoved
        ? ""
        : previousImageUrl;
  const [data, setData] = useState({
    quantity: lineItem?.quantity || 1,
    note: lineItem?.note || "",
    description: lineItem?.itemJson.description || "",
    category: lineItem?.itemJson.category || "",
    price: lineItem?.itemJson.price || "10",
    width: lineItem?.itemJson.sizing?.dimensions?.width || 0,
    height: lineItem?.itemJson.sizing?.dimensions?.height || 0,
    sqft: lineItem?.itemJson.sizing?.dimensions?.sqft || 0,
    overallWidth: lineItem?.itemJson.sizing?.dimensions?.overallWidth || 0,
    overallHeight: lineItem?.itemJson.sizing?.dimensions?.overallHeight || 0,
    forceOversized: lineItem?.itemJson.forceOversized || false,
    skipTax: lineItem?.itemJson.skipTax || false,
    excludeFromShipping: lineItem?.itemJson.excludeFromShipping || false,
    usesFrame: lineItem?.itemJson.usesFrame || false,
    usesStretcher: lineItem?.itemJson.usesStretcher || false,
    conditionalFields: lineItem?.itemJson.cartData.conditionalFields || [],
    materialsField1: lineItem?.itemJson.cartData.materialsField1 || "",
    materialsField2: lineItem?.itemJson.cartData.materialsField2 || "",
    materialsField3: lineItem?.itemJson.cartData.materialsField3 || "",
    materialsField4: lineItem?.itemJson.cartData.materialsField4 || "",
    materialsField5: lineItem?.itemJson.cartData.materialsField5 || "",
    materialsField5_5: lineItem?.itemJson.cartData.materialsField5_5 || "",
    materialsField6: lineItem?.itemJson.cartData.materialsField6 || "",
    materialsField7: lineItem?.itemJson.cartData.materialsField7 || "",
    materialsField8: lineItem?.itemJson.cartData.materialsField8 || "",
    materialsField9: lineItem?.itemJson.cartData.materialsField9 || "",
    materialsField10: lineItem?.itemJson.cartData.materialsField10 || "",
    staticField1: lineItem?.itemJson.cartData.staticField1 || "",
    staticField2: lineItem?.itemJson.cartData.staticField2 || "",
    staticField3: lineItem?.itemJson.cartData.staticField3 || "",
    staticField4: lineItem?.itemJson.cartData.staticField4 || "",
    fileName: lineItem?.itemJson.fileName || "",
  });

  const priceNumber = isNaN(Number(data.price)) ? 0 : Number(data.price);
  const update = (payload: Partial<typeof data>) =>
    setData(prev => ({ ...prev, ...payload }));

  // ── Framing spec (drives the Master List coded summary) ──────────────────
  // Staff pick plain-English options; the live engine emits the short codes.
  // Every value below is written into itemJson and read by woItemToken.
  const ij: any = lineItem?.itemJson || {};
  const [spec, setSpec] = useState({
    productType: (ij.productType as string) || "Framed Print",
    customOrder: !lineItem
      ? ""
      : ij.productType === "Scan"
        ? "scan"
        : ij.productType === "Shipping"
          ? "shipping"
          : /mount/i.test((ij.treatmentOptions?.type as string) || "")
            ? "mounting"
            : ij.productType === "Framed Print"
              ? "framing"
              : "printing",
    printOn:
      ij.productType === "Canvas"
        ? "Canvas"
        : ij.productType === "HD Aluminum"
          ? "HD Aluminum"
          : "Fine Art Print",
    media: ij.supplied
      ? SUPPLIED_MEDIA_VALUE
      : (ij.paperOptions?.paper as string) || "",
    suppliedMatted: !!ij.suppliedMatted,
    setBack: !!ij.frameOptions?.setBack,
    treatment: (ij.treatmentOptions?.type as string) || "",
    frameLabel: (ij.frameOptions?.label as string) || "",
    customMoulding: "",
    // NO default: this used to fall back to "Regular Acrylic", which stamped
    // glazing onto every Create-Item line whether or not anyone chose it — the
    // summary then told the bench to cut acrylic for jobs that need none.
    acrylic: (ij.frameOptions?.glass?.type as string) || "",
    matWidth: String(ij.frameOptions?.matboard?.size || ""),
    matPly: (ij.frameOptions?.matboard?.ply as string) || "",
    matColor: (ij.frameOptions?.matboard?.color as string) || "",
    customMat: "",
    reveal: String(ij.frameOptions?.reveal || ""),
    backingColor: (ij.frameOptions?.backing?.color as string) || "",
    customBacking: "",
    // Canvas construction/edge (Create-Item):
    edgeType: ij.paperOptions?.mirrored
      ? "mirror"
      : ij.paperOptions?.border
        ? "border"
        : "",
    borderColor: (ij.paperOptions?.borderColor as string) || "",
    customBorderColor: "",
    borderSize: String(ij.paperOptions?.size || ""),
    finish: (ij.type as string) || "",
    deckle: ij.paperOptions?.style === 3,
    substrate: (ij.mountSubstrate as string) || "",
    brace: !!ij.brace,
    cleat: !!ij.cleat,
    // Shipping line (10-19, Michael): staff already have the destination (it
    // is written into the line body), so the panel asks the ONE thing the
    // cart needs to know — is it taxable (NY) or not. Remembered on the line.
    shipsTo: (ij.shipsTo as string) || "",
  });
  const updateSpec = (p: Partial<typeof spec>) =>
    setSpec(prev => ({ ...prev, ...p }));
  // Custom Order category -> productType (+ treatment). Printing resolves its
  // productType from the Print-on substrate; the others map directly.
  const selectCustomOrder = (v: string) => {
    const p: Partial<typeof spec> = { customOrder: v };
    if (v === "framing") p.productType = "Framed Print";
    else if (v === "printing") {
      p.productType = spec.printOn || "Fine Art Print";
      p.treatment = p.productType === "Fine Art Print" ? "Print Only" : "";
    } else if (v === "mounting") {
      p.productType = "Fine Art Print";
      p.treatment = "Mounted (on substrate)";
    } else if (v === "scan") p.productType = "Scan";
    else if (v === "shipping") p.productType = "Shipping";
    updateSpec(p);
  };
  const selectPrintOn = (v: string) => {
    updateSpec({
      printOn: v,
      productType: v,
      treatment: v === "Fine Art Print" ? "Print Only" : "",
    });
  };
  const isSupplied = spec.media === SUPPLIED_MEDIA_VALUE;
  const isCanvas = spec.productType === "Canvas";
  const isCanvasFloater = isCanvas && /floater|frame/i.test(spec.treatment);
  const isLooseCanvas = isCanvas && /loose/i.test(spec.treatment);
  const isHDAluminum = spec.productType === "HD Aluminum";
  const isHDFloater = isHDAluminum && /floater|float/i.test(spec.treatment);
  const isMounted = /mount/i.test(spec.treatment);
  // Custom Order category drives which fields show.
  const isPrinting = spec.customOrder === "printing";
  const isFraming = spec.customOrder === "framing";
  const isMounting = spec.customOrder === "mounting";
  const isService =
    spec.customOrder === "scan" || spec.customOrder === "shipping";
  const isShippingLine = spec.customOrder === "shipping";
  // Size is OPTIONAL (Michael, 2026-08-21): a line often covers multiple pieces,
  // and a single width/height is meaningless there. The summary engine already
  // handles no-size lines (size omitted / "custom — see invoice"), so nothing
  // downstream needs a number here. Do not reintroduce a hard requirement.
  const isPaperPrint = isPrinting && spec.productType === "Fine Art Print";
  const showFrame = [
    "Straight Fit",
    "Matted",
    "Shadow Box",
    "Exhibition Shadow Box",
    "Float Mount",
  ].includes(spec.treatment);
  const showMat = [
    "Matted",
    "Shadow Box",
    "Exhibition Shadow Box",
    "Mat Only",
  ].includes(spec.treatment);
  const showSuppliedMat = ["Matted", "Mat Only"].includes(spec.treatment);
  const showSetBack =
    isSupplied && ["Straight Fit", "Matted"].includes(spec.treatment);
  const showReveal = spec.treatment === "Float Mount";
  const PAPER_MEDIA = MEDIA_OPTIONS.filter(
    o => o.value !== "Exhibition Matte Canvas" && o.value !== "Metallic Canvas"
  );
  const FRAMING_TREATMENTS = TREATMENT_OPTIONS.filter(
    o => !["Mounted (on substrate)", "Print Only"].includes(o.value)
  );
  const derivedCategory = PRODUCT_TYPE_TO_CATEGORY[spec.productType] || "Other";
  // Display-only overall (outer) size for shipping — null when unknowable.
  const overall = computeOverallSize({
    productType: spec.productType,
    width: data.width,
    height: data.height,
    treatment: spec.treatment,
    frameLabel: spec.frameLabel,
    matWidth: spec.matWidth,
    reveal: spec.reveal,
  });

  // Flattened frame options for the grouped Select.
  const FRAME_SELECT_DATA = FRAME_GROUPS.map(g => ({
    group: g.group,
    items: g.items.map(o => ({ value: o.value, label: o.label })),
  }));

  // Canvas / HD floater picker = floater group + custom moulding.
  const CANVAS_FRAME_SELECT_DATA = FRAME_GROUPS.filter(g =>
    /floater|other/i.test(g.group)
  ).map(g => ({
    group: g.group,
    items: g.items.map(o => ({ value: o.value, label: o.label })),
  }));

  async function submit() {
    setIsSubmitting(true);
    try {
    if (previousImageUrl && fileRemoved) {
      // Best-effort cleanup of the item's old file. The delete route refuses
      // (409) files a live cart still references — which is THIS cart until
      // the update lands — so a refusal must not block the update
      // (2026-09-03: "Request failed with status code 409" on Update Item).
      try {
        await UploadsApi.deleteFileByUrl(previousImageUrl);
      } catch (err: any) {
        if (err?.response?.status !== 409) throw err;
        console.info("[line-item] previous file kept (still referenced)");
      }
    }
    const newFileUrl = file ? await UploadsApi.uploadFileToS3(file) : "";

    if (newFileUrl) {
      await new Promise<void>(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = newFileUrl;
      });
    }

    const imageUrl = newFileUrl
      ? newFileUrl
      : selectedUploadedImage
        ? selectedUploadedImage.url
        : fileRemoved
          ? ""
          : previousImageUrl || "";

    const rawImageUrl = newFileUrl
      ? newFileUrl.replace("_compressed.", "_raw.")
      : selectedUploadedImage
        ? selectedUploadedImage.rawUrl
        : fileRemoved
          ? ""
          : lineItem?.itemJson.rawImageUrl || imageUrl;

    const compressedImageUrl = newFileUrl
      ? newFileUrl
      : selectedUploadedImage
        ? selectedUploadedImage.url
        : fileRemoved
          ? ""
          : lineItem?.itemJson.compressedImageUrl || imageUrl;

    const resolvedFileName = selectedUploadedImage
      ? selectedUploadedImage.name
      : data.fileName;

    // Derive the framing-spec itemJson the summary engine reads. Uses-frame /
    // uses-stretcher (finishing columns) are auto-derived here — the manual
    // switches were removed as redundant.
    const frameLabelOut =
      spec.frameLabel === CUSTOM_VALUE
        ? spec.customMoulding.trim() || "custom moulding"
        : spec.frameLabel;
    const matColorOut =
      spec.matColor === CUSTOM_VALUE ? "custom" : spec.matColor;
    const backingColorOut =
      spec.backingColor === CUSTOM_VALUE ? "custom" : spec.backingColor;
    // 10-19 (Michael): a Canvas / HD-alu line is framed ONLY on the floater
    // treatment. The visualizer copies a default frame label ("White Gallery
    // Frame", or a floater browsed past) onto EVERY canvas line, so trusting
    // the label stamped usesFrame on stretcher-only canvases the moment they
    // were opened in Update Item → phantom frames on the Master List (the
    // summary-text side of this was fixed 08-12, T-50653/54/95).
    const derivedUsesFrame =
      !!spec.frameLabel &&
      (FRAMED_TREATMENTS.has(spec.treatment) ||
        ((isCanvas || isHDAluminum) && /float/i.test(spec.treatment)));
    const derivedUsesStretcher =
      spec.productType === "Canvas" && /float|stretch/i.test(spec.treatment);
    // 2026-10-10: the WRITER says how a piece ships (same contract as the
    // Claude tab's build_cart) so checkout never has to infer it from frame
    // fields. Read by the BE classifier (pieceClassifier.classifyItemPiece).
    // Unknown (Custom / no treatment / scan / shipping) = no stamp → the
    // classifier infers as before. Always written, so an edit that changes
    // the treatment replaces a stale stamp.
    const t = spec.treatment;
    const derivedShipKind: "print" | "mat-only" | "framed" | undefined =
      isService
        ? undefined
        : isCanvas
          ? /loose/i.test(t)
            ? "print"
            : t
              ? "framed"
              : undefined
          : isHDAluminum || isMounted
            ? "framed"
            : /print\s*only|no\s*frame/i.test(t)
              ? "print"
              : t === "Mat Only"
                ? "mat-only"
                : FRAMED_TREATMENTS.has(t)
                  ? "framed"
                  : undefined;
    // Plain-English cart/invoice breakdown (customer-facing; no internal codes).
    const customerFields = deriveCustomerFacingFields(
      spec,
      data.width,
      data.height
    );

    const payload = {
      quantity: data.quantity,
      note: data.note,
      cartId: cartId,
      adminCustom: true,
      itemJson: {
        ...lineItem?.itemJson,
        price: data.price,
        imageUrl: imageUrl,
        rawImageUrl: rawImageUrl,
        compressedImageUrl: compressedImageUrl,
        description: editor?.getHTML() || "",
        category: derivedCategory as LineItemCategory,
        sizing: {
          ...lineItem?.itemJson.sizing,
          dimensions: {
            ...lineItem?.itemJson.sizing?.dimensions,
            width: data.width,
            height: data.height,
            sqft: data.sqft,
            overallWidth: overall ? overall.width : data.overallWidth,
            overallHeight: overall ? overall.height : data.overallHeight,
          },
        },
        cartData: {
          ...lineItem?.itemJson.cartData,
          conditionalFields: customerFields.conditionalFields,
          materialsField1: data.materialsField1,
          materialsField2: data.materialsField2,
          materialsField3: data.materialsField3,
          materialsField4: data.materialsField4,
          materialsField5: data.materialsField5,
          materialsField5_5: data.materialsField5_5,
          materialsField6: data.materialsField6,
          materialsField7: data.materialsField7,
          materialsField8: data.materialsField8,
          materialsField9: data.materialsField9,
          materialsField10: data.materialsField10,
          staticField1: data.staticField1,
          staticField2: customerFields.staticField2,
          staticField3: customerFields.staticField3,
          staticField4: customerFields.staticField4,
        },
        fileName: resolvedFileName,
        forceOversized: data.forceOversized,
        skipTax: data.skipTax,
        // 10-19 (T-51898_B): a Scan / Shipping line is not a box. Left at
        // false, the BE classifier saw a piece with no dims and no shipKind
        // ("unmeasured"), routed the cart to the Multiple Items quote path and
        // taxed it as NY. Same stamp build_cart already puts on service lines.
        excludeFromShipping: isService ? true : data.excludeFromShipping,
        shipsTo: isShippingLine
          ? (spec.shipsTo as "ny" | "outside") || undefined
          : undefined,
        usesFrame: derivedUsesFrame,
        usesStretcher: derivedUsesStretcher,
        shipKind: derivedShipKind,
        // ── framing spec → coded Master List summary ──
        productType: spec.productType,
        ...(isHDAluminum ? { type: spec.finish || undefined } : {}),
        ...(isMounted
          ? {
              mountSubstrate: spec.substrate || undefined,
              brace: spec.brace,
              cleat: spec.cleat,
            }
          : {}),
        supplied: isSupplied,
        suppliedMatted: isSupplied && spec.suppliedMatted,
        treatmentOptions: {
          ...lineItem?.itemJson?.treatmentOptions,
          type: spec.treatment || undefined,
        },
        paperOptions: {
          ...lineItem?.itemJson?.paperOptions,
          paper: isSupplied ? undefined : spec.media || undefined,
          style: spec.deckle ? 3 : 0,
          ...(spec.productType === "Canvas"
            ? {
                mirrored: spec.edgeType === "mirror",
                border: spec.edgeType === "border",
                borderColor:
                  spec.edgeType === "border"
                    ? (spec.borderColor === CUSTOM_VALUE
                        ? spec.customBorderColor.trim()
                        : spec.borderColor) || undefined
                    : undefined,
                size:
                  /loose/i.test(spec.treatment) && spec.edgeType === "border"
                    ? Number(spec.borderSize) || 0
                    : 0,
              }
            : {}),
        },
        frameOptions:
          isCanvas || isHDAluminum
            ? {
                ...lineItem?.itemJson?.frameOptions,
                label: frameLabelOut || undefined,
              }
            : {
                ...lineItem?.itemJson?.frameOptions,
                label: frameLabelOut || undefined,
                setBack: isSupplied && spec.setBack ? true : undefined,
                glass: {
                  ...lineItem?.itemJson?.frameOptions?.glass,
                  type: spec.acrylic || undefined,
                },
                reveal: Number(spec.reveal) || 0,
                matboard: {
                  ...lineItem?.itemJson?.frameOptions?.matboard,
                  size: Number(spec.matWidth) || 0,
                  ply: spec.matPly || undefined,
                  color: matColorOut || undefined,
                  customDesc:
                    spec.matColor === CUSTOM_VALUE
                      ? spec.customMat.trim() || undefined
                      : undefined,
                },
                backing: {
                  ...lineItem?.itemJson?.frameOptions?.backing,
                  color: backingColorOut || undefined,
                  customDesc:
                    spec.backingColor === CUSTOM_VALUE
                      ? spec.customBacking.trim() || undefined
                      : undefined,
                },
              },
      },
    } satisfies DeepPartial<LineItem>;

    if (!lineItem) await Crud.lineItems.create(payload);
    else if (lineItem) await Crud.lineItems.update(lineItem.id, payload);

    // 10-19 (Michael): a Shipping line IS the shipping — same rule the
    // Claude tab's build_cart has used since 09-10. Saving one turns
    // "Turn off shipping calculations" on, and "Turn off sales tax" on when
    // the destination is outside New York (cart-level, so prints in the same
    // cart are untaxed too). Nothing is ever turned OFF here — a deliberate
    // exemption on the cart survives a later "New York" answer.
    if (isShippingLine) {
      await Crud.carts.update(cartId, {
        skipShipping: true,
        ...(spec.shipsTo === "outside" ? { taxExempt: true } : {}),
      } as any);
    }

    await onSubmit?.();
    } catch (error) {
      // Upload/save failures used to leave the button spinning forever
      // (Sentry 1S) — surface them and always reset the pending state.
      console.error("[line-item] submit failed", error);
      (window as any).Sentry?.captureException(error);
      toast.show({
        message:
          error instanceof Error && error.message
            ? error.message
            : "Something went wrong saving this item — please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    toolbarRef.current
      ?.querySelectorAll<HTMLElement>("button, a, [tabindex]")
      .forEach(el => el.setAttribute("tabindex", "-1"));
  }, []);

  useEffect(() => {
    UploadsService.getUploads().catch(() => {});
  }, []);

  useEffect(() => {
    if (!uploadsDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        uploadsDropdownRef.current &&
        !uploadsDropdownRef.current.contains(e.target as Node)
      ) {
        setUploadsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [uploadsDropdownOpen]);

  const renderImageOption = (img: ImageFile) => {
    const isSelected = selectedUploadedImage?.url === img.url;
    return (
      <button
        key={img.url}
        type="button"
        onClick={() => {
          setSelectedUploadedImage(img);
          setFile(null);
          setFileRemoved(false);
          setUploadsDropdownOpen(false);
        }}
        className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
          isSelected ? "bg-[#e6f7f7]" : ""
        }`}
      >
        <img
          src={img.thumbnailUrl || img.url}
          alt={img.name}
          className="size-8 shrink-0 rounded object-cover"
        />
        <span className="truncate">{img.name}</span>
      </button>
    );
  };

  const hasSelectableImages =
    REUSABLE_IMAGES.length > 0 || uploadedImages.length > 0;

  return (
    <div>
      <Section title="General Info:" contentClassName="grid gap-2" shown>
        <TextInput
          label="Name"
          required
          value={data.staticField1}
          onChange={e => update({ staticField1: e.target.value })}
        />
        <RichTextEditor editor={editor}>
          <div ref={toolbarRef}>
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Bold />
                <RichTextEditor.Italic />
                <RichTextEditor.Underline />
                <RichTextEditor.Strikethrough />
                <RichTextEditor.ClearFormatting />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.AlignLeft />
                <RichTextEditor.AlignCenter />
                <RichTextEditor.AlignRight />
                <RichTextEditor.AlignJustify />
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Link />
                <RichTextEditor.Unlink />
              </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>
          </div>

          <RichTextEditor.Content />
        </RichTextEditor>

        <FileInput
          label="Upload new file"
          required
          className="overflow-hidden"
          placeholder={
            filePreviewUrl && !selectedUploadedImage
              ? filePreviewUrl.slice(0, 50) + "..."
              : "Click to upload file"
          }
          value={file}
          onChange={f => {
            if (f) {
              setFile(f);
              setSelectedUploadedImage(null);
              update({ fileName: f.name });
            }
          }}
          rightSection={
            <Button
              variant="subtle"
              size="compact-xs"
              className="h-full"
              color="red"
              onClick={() => {
                setFile(null);
                setFileRemoved(true);
                update({ fileName: "" });
              }}
            >
              <IconX className="size-5" />
            </Button>
          }
        />

        <div>
          <p className="mb-1 text-sm font-medium">
            Or select from uploaded images
          </p>
          <div ref={uploadsDropdownRef} className="relative">
            {/* Trigger */}
            <button
              type="button"
              onClick={() => setUploadsDropdownOpen(o => !o)}
              className="flex w-full items-center gap-2 rounded border border-[#ced4da] bg-white px-3 py-2 text-left text-sm hover:border-[#adb5bd]"
            >
              {selectedUploadedImage ? (
                <>
                  <img
                    src={
                      selectedUploadedImage.thumbnailUrl ||
                      selectedUploadedImage.url
                    }
                    alt={selectedUploadedImage.name}
                    className="size-6 rounded object-cover"
                  />
                  <span className="flex-1 truncate">
                    {selectedUploadedImage.name}
                  </span>
                  <span
                    role="button"
                    className="ml-auto shrink-0 text-gray-400 hover:text-red-500"
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedUploadedImage(null);
                    }}
                  >
                    <IconX className="size-4" />
                  </span>
                </>
              ) : (
                <span className="text-gray-400">
                  {hasSelectableImages
                    ? "Select an image..."
                    : "No images found"}
                </span>
              )}
            </button>

            {/* Dropdown list */}
            {uploadsDropdownOpen && hasSelectableImages && (
              <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded border border-[#ced4da] bg-white shadow-md">
                {REUSABLE_IMAGES.length > 0 && (
                  <>
                    <p className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Reusable
                    </p>
                    {REUSABLE_IMAGES.map(renderImageOption)}
                  </>
                )}
                {uploadedImages.length > 0 && (
                  <>
                    <p className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Your uploads
                    </p>
                    {uploadedImages.map(renderImageOption)}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {filePreviewUrl && (
          <div className="grid place-items-center">
            <img src={filePreviewUrl} />
          </div>
        )}

        <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-x-4">
          <TextInput
            label="Price"
            rightSection="$"
            value={data.price}
            onChange={e => update({ price: e.target.value })}
          />
          <NumberInput
            label="Quantity"
            value={data.quantity}
            min={0}
            onChange={value => update({ quantity: value as number })}
          />
          <TextInput
            label="Total"
            readOnly
            rightSection="$"
            value={priceNumber * data.quantity}
          />
        </div>
        <Textarea
          label="Note"
          value={data.note}
          onChange={e => update({ note: e.target.value })}
        />
      </Section>
      <hr />
      <Section title="Custom Item" shown>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <Select
            label="Custom Order"
            placeholder="Choose type…"
            data={CUSTOM_ORDER_OPTIONS}
            value={spec.customOrder}
            onChange={v => selectCustomOrder(v || "")}
            // A second click on the ticked option must not clear it (10-12: the
            // box kept showing "Framing" while the spec reset → fields vanished).
            allowDeselect={false}
            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
          />

          {isService && (
            <p className="mt-3 text-sm text-gray-600">
              No spec needed — Name, price and quantity above cover this line. The
              Master List reads “custom — see invoice”. If the job also prints or
              frames, add those as their own line items.
            </p>
          )}

          {isShippingLine && (
            <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
              <Select
                label="Ships to"
                placeholder="Choose…"
                required
                data={[
                  { value: "ny", label: "New York — sales tax applies" },
                  {
                    value: "outside",
                    label: "Outside New York — no sales tax",
                  },
                ]}
                value={spec.shipsTo}
                onChange={v => updateSpec({ shipsTo: v || "" })}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true, zIndex: 9999 }}
              />
              <p className="mt-2 text-xs text-gray-600">
                Saving this line turns “Turn off shipping calculations” on for
                the cart (this line is the shipping). “Outside New York” also
                turns “Turn off sales tax” on.
              </p>
            </div>
          )}

          {(isPrinting || isFraming || isMounting) && (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <NumberInput
                  label="Width (in)"
                  value={data.width}
                  min={0}
                  decimalScale={3}
                  onChange={v => update({ width: v as number })}
                />
                <NumberInput
                  label="Height (in)"
                  value={data.height}
                  min={0}
                  decimalScale={3}
                  onChange={v => update({ height: v as number })}
                />
              </div>

              {/* ── PRINTING ── bare, unframed output (paper / canvas / aluminum) */}
              {isPrinting && (
                <>
                  <div className="mt-3">
                    <Select
                      label="Print on"
                      data={PRINT_ON_OPTIONS}
                      value={spec.printOn}
                      onChange={v => selectPrintOn(v || "Fine Art Print")}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Bare, unframed output. Framing this piece? Use the “Framing”
                      type instead.
                    </p>
                  </div>

                  {isPaperPrint && (
                    <>
                      <div className="mt-3">
                        <Select
                          label="Media"
                          data={PAPER_MEDIA}
                          value={spec.media}
                          searchable
                          onChange={v => updateSpec({ media: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                      </div>
                      <div className="mt-3">
                        <Checkbox
                          label="Deckle edge (hand-torn)"
                          checked={spec.deckle}
                          onChange={e =>
                            updateSpec({ deckle: e.currentTarget.checked })
                          }
                          color="#4AA0A0"
                        />
                      </div>
                    </>
                  )}

                  {isCanvas && (
                    <>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <Select
                          label="Construction"
                          placeholder="Choose construction…"
                          data={CANVAS_CONSTRUCTION_OPTIONS}
                          value={spec.treatment}
                          onChange={v => updateSpec({ treatment: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                        <Select
                          label="Canvas media"
                          placeholder="Choose media…"
                          data={CANVAS_MEDIA_OPTIONS}
                          value={spec.media}
                          onChange={v => updateSpec({ media: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                      </div>

                      {!isMounted && (
                        <>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <Select
                          label="Edge / border"
                          placeholder="Choose edge…"
                          data={CANVAS_EDGE_OPTIONS}
                          value={spec.edgeType}
                          onChange={v => updateSpec({ edgeType: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                        {spec.edgeType === "border" && (
                          <Select
                            label="Border color"
                            placeholder="Choose color…"
                            data={CANVAS_BORDER_COLOR_OPTIONS}
                            value={spec.borderColor}
                            onChange={v => updateSpec({ borderColor: v || "" })}
                            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                          />
                        )}
                      </div>

                      {spec.edgeType === "border" &&
                        spec.borderColor === CUSTOM_VALUE && (
                          <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                            <TextInput
                              label="Custom border color"
                              placeholder="e.g. warm gray"
                              value={spec.customBorderColor}
                              onChange={e =>
                                updateSpec({ customBorderColor: e.target.value })
                              }
                            />
                          </div>
                        )}

                      {isLooseCanvas && spec.edgeType === "border" && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <TextInput
                            label="Border size"
                            placeholder={'e.g. 2"'}
                            value={spec.borderSize}
                            onChange={e =>
                              updateSpec({ borderSize: e.target.value })
                            }
                          />
                          <div />
                        </div>
                      )}
                        </>
                      )}

                      {isMounted && (
                        <>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <Select
                              label="Substrate"
                              placeholder="Choose substrate…"
                              data={MOUNT_SUBSTRATE_OPTIONS}
                              value={spec.substrate}
                              onChange={v => updateSpec({ substrate: v || "" })}
                              comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                            />
                            <div />
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <Checkbox
                              label="Wood brace (for hanging)"
                              checked={spec.brace}
                              onChange={e =>
                                updateSpec({ brace: e.currentTarget.checked })
                              }
                              color="#4AA0A0"
                            />
                            <Checkbox
                              label="French cleat"
                              checked={spec.cleat}
                              onChange={e =>
                                updateSpec({ cleat: e.currentTarget.checked })
                              }
                              color="#4AA0A0"
                            />
                          </div>
                        </>
                      )}

                      {isCanvasFloater && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <Select
                            label="Floater frame"
                            placeholder="Choose frame…"
                            data={CANVAS_FRAME_SELECT_DATA}
                            value={spec.frameLabel}
                            searchable
                            onChange={v => updateSpec({ frameLabel: v || "" })}
                            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                          />
                          <div />
                        </div>
                      )}

                      {isCanvasFloater && spec.frameLabel === CUSTOM_VALUE && (
                        <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                          <TextInput
                            label="Custom frame name / #"
                            placeholder="e.g. Larson 297 walnut floater"
                            value={spec.customMoulding}
                            onChange={e =>
                              updateSpec({ customMoulding: e.target.value })
                            }
                          />
                        </div>
                      )}
                    </>
                  )}

                  {isHDAluminum && (
                    <>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <Select
                          label="Finish"
                          placeholder="Choose finish…"
                          data={HD_ALUMINUM_FINISH_OPTIONS}
                          value={spec.finish}
                          onChange={v => updateSpec({ finish: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                        <Select
                          label="Mount"
                          placeholder="Choose mount…"
                          data={HD_ALUMINUM_MOUNT_OPTIONS}
                          value={spec.treatment}
                          onChange={v => updateSpec({ treatment: v || "" })}
                          comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                        />
                      </div>

                      {isHDFloater && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <Select
                            label="Floater frame"
                            placeholder="Choose frame…"
                            data={CANVAS_FRAME_SELECT_DATA}
                            value={spec.frameLabel}
                            searchable
                            onChange={v => updateSpec({ frameLabel: v || "" })}
                            comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                          />
                          <div />
                        </div>
                      )}

                      {isHDFloater && spec.frameLabel === CUSTOM_VALUE && (
                        <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                          <TextInput
                            label="Custom frame name / #"
                            placeholder="e.g. Larson 297 walnut floater"
                            value={spec.customMoulding}
                            onChange={e =>
                              updateSpec({ customMoulding: e.target.value })
                            }
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── FRAMING ── framed piece (supplied art or a print) */}
              {isFraming && (
                <>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Select
                      label="Media"
                      data={PAPER_MEDIA}
                      value={spec.media}
                      searchable
                      onChange={v => updateSpec({ media: v || "" })}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                    <Select
                      label="Treatment"
                      data={FRAMING_TREATMENTS}
                      value={spec.treatment}
                      onChange={v => updateSpec({ treatment: v || "" })}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                  </div>

                  {showFrame && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <Select
                        label="Frame / moulding"
                        data={FRAME_SELECT_DATA}
                        value={spec.frameLabel}
                        searchable
                        onChange={v => updateSpec({ frameLabel: v || "" })}
                        comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                      />
                      <Select
                        label="Acrylic / glazing"
                        data={ACRYLIC_OPTIONS}
                        value={spec.acrylic}
                        onChange={v => updateSpec({ acrylic: v || "" })}
                        comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                      />
                    </div>
                  )}

                  {showFrame && spec.frameLabel === CUSTOM_VALUE && (
                    <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                      <TextInput
                        label="Custom moulding name / #"
                        placeholder="e.g. Larson 297 walnut"
                        value={spec.customMoulding}
                        onChange={e =>
                          updateSpec({ customMoulding: e.target.value })
                        }
                      />
                    </div>
                  )}

                  {showSuppliedMat && (
                    <div className="mt-3">
                      <Checkbox
                        label="Customer supplied the mat (reads “suppl’d matted”)"
                        checked={spec.suppliedMatted}
                        onChange={e =>
                          updateSpec({ suppliedMatted: e.currentTarget.checked })
                        }
                        color="#4AA0A0"
                      />
                    </div>
                  )}

                  {showSetBack && (
                    <div className="mt-3">
                      <Checkbox
                        label="Set back"
                        checked={spec.setBack}
                        onChange={e =>
                          updateSpec({ setBack: e.currentTarget.checked })
                        }
                        color="#4AA0A0"
                      />
                    </div>
                  )}

                  <div className="mt-3">
                    <Checkbox
                      label="Deckle edge (hand-torn)"
                      checked={spec.deckle}
                      onChange={e =>
                        updateSpec({ deckle: e.currentTarget.checked })
                      }
                      color="#4AA0A0"
                    />
                  </div>

                  {showMat && (
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      <TextInput
                        label="Mat width"
                        placeholder={'e.g. 2"'}
                        value={spec.matWidth}
                        onChange={e => updateSpec({ matWidth: e.target.value })}
                      />
                      <Select
                        label="Mat ply"
                        data={MAT_PLY_OPTIONS}
                        value={spec.matPly}
                        onChange={v => updateSpec({ matPly: v || "" })}
                        comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                      />
                      <Select
                        label="Mat color"
                        data={MAT_COLOR_OPTIONS}
                        value={spec.matColor}
                        onChange={v => updateSpec({ matColor: v || "" })}
                        comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                      />
                    </div>
                  )}

                  {showMat && spec.matColor === CUSTOM_VALUE && (
                    <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                      <TextInput
                        label="Custom matboard description"
                        placeholder="e.g. suede oxblood 8-ply"
                        value={spec.customMat}
                        onChange={e => updateSpec({ customMat: e.target.value })}
                      />
                    </div>
                  )}

                  {showReveal && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <TextInput
                        label="Reveal"
                        placeholder={'e.g. 1.5"'}
                        value={spec.reveal}
                        onChange={e => updateSpec({ reveal: e.target.value })}
                      />
                      <Select
                        label="Backing color"
                        data={BACKING_COLOR_OPTIONS}
                        value={spec.backingColor}
                        onChange={v => updateSpec({ backingColor: v || "" })}
                        comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                      />
                    </div>
                  )}

                  {showReveal && spec.backingColor === CUSTOM_VALUE && (
                    <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                      <TextInput
                        label="Custom backing description"
                        placeholder="e.g. linen wrapped"
                        value={spec.customBacking}
                        onChange={e =>
                          updateSpec({ customBacking: e.target.value })
                        }
                      />
                    </div>
                  )}
                </>
              )}

              {/* ── MOUNTING ── mounted on a substrate */}
              {isMounting && (
                <>
                  <div className="mt-3">
                    <Select
                      label="What’s mounted (optional)"
                      data={[
                        { value: "", label: "— (name / price covers it)" },
                        ...MEDIA_OPTIONS,
                      ]}
                      value={spec.media}
                      searchable
                      onChange={v => updateSpec({ media: v || "" })}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Select
                      label="Substrate"
                      placeholder="Choose substrate…"
                      data={MOUNT_SUBSTRATE_OPTIONS}
                      value={spec.substrate}
                      onChange={v => updateSpec({ substrate: v || "" })}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                    <Select
                      label="Floater frame (optional)"
                      data={CANVAS_FRAME_SELECT_DATA}
                      value={spec.frameLabel}
                      searchable
                      onChange={v => updateSpec({ frameLabel: v || "" })}
                      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                    />
                  </div>

                  {spec.frameLabel === CUSTOM_VALUE && (
                    <div className="mt-3 rounded-lg border border-[#a9c6f5] bg-[#eef4ff] p-3">
                      <TextInput
                        label="Custom frame name / #"
                        placeholder="e.g. Larson 297 walnut floater"
                        value={spec.customMoulding}
                        onChange={e =>
                          updateSpec({ customMoulding: e.target.value })
                        }
                      />
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Checkbox
                      label="Wood brace (for hanging)"
                      checked={spec.brace}
                      onChange={e =>
                        updateSpec({ brace: e.currentTarget.checked })
                      }
                      color="#4AA0A0"
                    />
                    <Checkbox
                      label="French cleat"
                      checked={spec.cleat}
                      onChange={e =>
                        updateSpec({ cleat: e.currentTarget.checked })
                      }
                      color="#4AA0A0"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Section>
      <hr />
      <div className="flex justify-between gap-4 p-4">
        {lineItem && (
          <Button
            variant="outline"
            color="red"
            leftSection={<Trash />}
            onClick={async () => {
              if (!lineItem) return;
              const isConfirmed = confirm(
                "Are you sure you want to delete this item?"
              );
              if (!isConfirmed) return;
              await Crud.lineItems.delete(lineItem.id);
              onSubmit?.();
            }}
          >
            Delete
          </Button>
        )}
        <Tooltip
          label="Please upload an image to continue"
          disabled={!!filePreviewUrl}
          withArrow
          zIndex={9999}
        >
          <span style={{ display: "inline-block", marginLeft: "auto" }}>
            <Button
              onClick={submit}
              disabled={
                !data.staticField1 ||
                !data.price ||
                !data.quantity ||
                !filePreviewUrl ||
                (isShippingLine && !spec.shipsTo)
              }
              leftSection={lineItem ? <NotePencil /> : <Plus />}
              loading={isSubmitting}
            >
              {lineItem ? "Update item" : "Create item"}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
